import axios from 'axios';
import { cachetime } from '../config';
import { getSightDistance } from './sightmap';
import { distanceFromSightToPoi } from '@/utils/map';
import poi from '../modules/poi';

const defaultFilter = {stars: [], districts: [], awards: []}
const PAGE_SIZE = 10;

export default function() {
  return {
    namespaced: true,
    state: {
      stars: {},
      filter: {},
      search: {
        locale: '',
        district: '',
        star: '',
        keyword: '',
        resultCount: 0,
        results: []
      },
      hotels: {},
      __updatedAt: {},
    },
    mutations: {
      SET_STARS(state, { data, locale}) {
        state.stars = {
          ...state.stars,
          [locale]: data,
        }
        state.__updatedAt[locale] = Date.now();
      },
      SET_FILTER(state, { data, locale }) {
        state.filter = {
          ...state.filter,
          [locale]: {
            data,
            __updatedAt: Date.now()
          },
        }
      },
      RESET_SEARCH(state, { district, star, keyword, locale}) {
        state.search = {
          district, star, keyword, locale,
          resultCount: 0,
          results: []
        }
      },
      REFRESH_SEARCH_RESULT(state, { resultCount }) {
        state.search = {
          ...state.search,
          resultCount,
          results: Array.apply(null, Array(resultCount))
        }
      },
      UPDATE_SEARCH_RESULT(state, { page, results }) {
        const START_IDX = (page - 1) * PAGE_SIZE;
        results.forEach((el, idx) => {
          state.search.results[START_IDX + idx] = {
            ...el
          };
        });
      },
      UPDATE_HOTEL(state, { data, id, locale }) {
        let allHotel = state.hotels[id] || {};
        state.hotels = {
          ...state.hotels,
          [id]: {
            ...allHotel,
            [locale]: {
              ...data,
              __updatedAt: Date.now()
            }
          }
        }
      }
    },
    actions: {
      FETCH_INTRODUCTION({ state, commit }, { locale }) {
        if (state.stars[locale]
          && state.__updatedAt[locale]
          && Date.now() - state.__updatedAt[locale] < cachetime 
        ) return ;

        return axios.get(encodeURI(`/api/accommodation`))
          .then((res) => {
            const data = res.data || {};
            const { stars=[] } = data;

            commit('SET_STARS', { data: stars, locale });
          }).catch(err => console.log(err));      
      },
      FETCH_FILTER({ state, commit }, { locale }) {
        if (state.filter[locale]
          && state.filter[locale].data
          && state.filter[locale].__updatedAt
          && Date.now() - state.filter[locale].__updatedAt < cachetime 
        ) return ;

        return axios.get(encodeURI(`/api/accommodation_filter`))
          .then((res) => {
            const data = res.data || defaultFilter;
            commit('SET_FILTER', { data, locale });
          }).catch(err => console.log(err));      
      },
      SEARCH_HOTEL({ state, commit }, { 
        district='', star='',
        keyword='', page=1, locale='en' 
      }) {
        if (district != state.search.district 
          || star != state.search.star 
          || keyword != state.search.keyword 
          || locale != state.search.locale
        ) {
          commit('RESET_SEARCH', { district, star, keyword, locale })
        } else if (!state.search.results.slice(PAGE_SIZE * (page - 1), PAGE_SIZE * page).some(e => e == null)) {
          return ;
        }

        return axios.get(encodeURI(`/api/accommodation?lang=${locale}&district=${district}&star=${star}&keyword=${keyword}`))
          .then((res) => {
            let { page=1, resultCount=0, results=[] } = res.data;
            if (resultCount != state.search.resultCount) {
              commit('REFRESH_SEARCH_RESULT', { resultCount });
            }
            
            if (results.length > 0) {
              commit('UPDATE_SEARCH_RESULT', { page, results });
            }
          }).catch(err => console.log(err));
      },
      FETCH_HOTEL({ state, commit, dispatch }, { id='', locale='en', ssr }) {
        if (state.hotels[id]
          && state.hotels[id][locale]
          && state.hotels[id][locale].__updatedAt
          && Date.now() - state.hotels[id][locale].__updatedAt < cachetime 
        ) {
          return ;
        }

        return axios.get(encodeURI(`/api/accommodation/${id}`))
          .then((res) => {
            const data = res.data || {};
            delete data.pois; // old api still contains pois field, delete to avoid vue reactivity issue
            // data.pois = [];
            // console.log('datapois: ', data.pois)
            const entityCoordinates = data.coordinate || [];
            const coordinate = Array.isArray(entityCoordinates[0]) ? entityCoordinates[0].join('|') : entityCoordinates.join('|');

            commit('UPDATE_HOTEL', {
              data,
              id,
              locale
            })

            // ssr no FETCH_POI
            if (ssr) {
              return;
            } else if (coordinate) {
              return dispatch('poi/FETCH_POI', { coordinate, locale })
                .then((pois) => {
                  data.pois = pois.filter(poi => poi.id !== data.id).map((poi) => {
                    return Object.assign(poi, {
                      distance: distanceFromSightToPoi(data, poi),
                    });
                  });

                  commit('UPDATE_HOTEL', {
                    data,
                    id,
                    locale
                  })
                })
                .catch(err => {
                  console.log(err);
                  // if (err.response && err.response.status == 404) {
                  throw err;
                  // }
                });
            } else {
              console.warn(id, 'missing coordinate data')
            }        
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          })
      }
    },
    getters: { 
      getSearchResult: (state, getters, rootState) => () => {
        let raw = [...state.search.results] || [];
        const { isGeolocationOn, userLocation } = rootState.geolocation;

        if (raw && isGeolocationOn) {
          // sort raw
          raw.sort((a, b) => getSightDistance(a, userLocation) - getSightDistance(b, userLocation));
        }

        return raw;
      },
      getExistSearchResult: (state, getters, rootState) => (
        id, options, packAsGroup=false
      ) => {
        // check options match
        let {
          district='', 
          keyword='', locale='en' 
        } = options;
        let star = options['class'] || '';

        if (district != state.search.district 
          || star != state.search.star 
          || keyword != state.search.keyword 
          || locale != state.search.locale
        ) {
          return []
        }

        // check target exist
        if (!state.search.results.find(o => o.id == id)) {
          return []
        }

        // return result
        const results = getters.getSearchResult(locale);
        return packAsGroup 
          ? {
            sights: results
          }
          : results; 
      },
      getStars: (state) => (locale) => {
        return state.stars[locale] 
          || new Array(8).fill().map((val, idx) => ({ 
            value: idx,
            thumbnail: '',
            name: ''
          }));;
      },
      getFilter: (state) => (locale) => {
        return state.filter[locale] 
          ? (state.filter[locale].data || defaultFilter) 
          : defaultFilter;
      },
      getHotel: (state) => (id, locale) => {
        let data = (state.hotels[id] && state.hotels[id][locale]) 
          || {
            gallery: []
          }

        data.filters = [];
        data.star && data.filters.push({
          name: data.star.name,
          to: `/accommodation/search?class=${data.star.value}`
        })

        if (data.award) {
          data.award = {
            ...data.award,
            icon: 'fas fa-medal'
          }
        }

        return data;
      }
    },
    modules: {
      poi: poi(),
    },
  }
}